import { X, Clock, Calendar, Flag, User, FolderKanban, Briefcase, FileText, Paperclip } from "lucide-react";
import type { ActivityTask } from "../../store/useActivityTaskStore";
import { fmtDuration } from "../../lib/activityTime";

// Read-only full details of a single activity — opens over the Activities page
// so the BD can review everything without navigating away.
export default function ActivityDetailsModal({ activity, onClose }: { activity: ActivityTask; onClose: () => void }) {
  const a = activity;
  const cat = typeof a.category === "object" ? a.category : null;
  const est = a.estimatedMinutes > 0 ? `${Math.floor(a.estimatedMinutes / 60)}h ${a.estimatedMinutes % 60}m` : "—";
  const due = a.dueDate ? new Date(a.dueDate).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—";
  const priorityClass = a.priority === "High" ? "text-rose-600" : a.priority === "Medium" ? "text-amber-600" : "text-slate-500";

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] flex items-center justify-center p-3 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-5 sm:px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0 pr-3">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              {cat && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: cat.color || "#2462b2" }}>{cat.name}</span>
              )}
              <span className={`text-[11px] font-semibold flex items-center gap-1 ${priorityClass}`}><Flag size={11} /> {a.priority}</span>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{a.status}</span>
            </div>
            <h2 className="text-lg font-bold text-slate-800 leading-snug">{a.title || a.description || "Untitled activity"}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-full transition-colors shrink-0"><X size={18} /></button>
        </div>

        <div className="cre-scroll overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
          {/* Quick facts */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Fact icon={Clock} label="Estimated" value={est} />
            <Fact icon={Calendar} label="Due Date" value={due} />
            <Fact icon={Clock} label="Time Logged" value={fmtDuration(a.totalSeconds || 0)} />
          </div>

          {/* Description */}
          <Section title="Description" icon={FileText}>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {a.aiEnhancedDescription || a.description || "No description provided."}
            </p>
          </Section>

          {a.aiEnhancedDescription && a.description && a.aiEnhancedDescription !== a.description && (
            <Section title="Original Note" icon={FileText}>
              <p className="text-sm text-slate-500 leading-relaxed whitespace-pre-wrap">{a.description}</p>
            </Section>
          )}

          {/* Linked records */}
          {(a.relatedLead || a.relatedProject || a.relatedJob || a.relatedProposal) && (
            <Section title="Linked To" icon={FolderKanban}>
              <div className="flex flex-col gap-1.5">
                {a.relatedLead && <LinkRow icon={User} label="Lead / Client" value={a.relatedLead.name} />}
                {a.relatedProject && <LinkRow icon={FolderKanban} label="Project" value={a.relatedProject.name} />}
                {a.relatedJob && <LinkRow icon={Briefcase} label="Job" value={a.relatedJob.jobTitle || "Untitled job"} />}
                {a.relatedProposal && <LinkRow icon={FileText} label="Proposal" value={a.relatedProposal.title || "Untitled proposal"} />}
              </div>
            </Section>
          )}

          {/* Notes */}
          {a.notes && (
            <Section title="Notes" icon={FileText}>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{a.notes}</p>
            </Section>
          )}

          {/* Attachments */}
          {a.attachments?.length > 0 && (
            <Section title={`Attachments (${a.attachments.length})`} icon={Paperclip}>
              <ul className="space-y-1.5">
                {a.attachments.map((f: any, i: number) => (
                  <li key={i}>
                    <a href={f.url || f.path || "#"} target="_blank" rel="noreferrer" className="text-sm text-[#2462b2] hover:underline inline-flex items-center gap-1.5 truncate">
                      <Paperclip size={13} /> {f.name || f.originalName || `Attachment ${i + 1}`}
                    </a>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>

        <div className="flex items-center justify-end px-5 sm:px-6 py-4 border-t border-slate-100 bg-slate-50/60 shrink-0">
          <button onClick={onClose} className="px-5 py-2 bg-[#2462b2] text-white rounded-lg hover:bg-[#1e52a0] transition-colors text-sm font-semibold">Close</button>
        </div>
      </div>
    </div>
  );
}

const Fact = ({ icon: Icon, label, value }: { icon: any; label: string; value: string }) => (
  <div className="border border-slate-200 rounded-lg p-2.5">
    <div className="text-[11px] font-semibold text-slate-400 flex items-center gap-1 mb-0.5"><Icon size={12} /> {label}</div>
    <div className="text-sm font-bold text-slate-800">{value}</div>
  </div>
);
const Section = ({ title, icon: Icon, children }: { title: string; icon: any; children: any }) => (
  <div>
    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-2"><Icon size={13} /> {title}</h3>
    {children}
  </div>
);
const LinkRow = ({ icon: Icon, label, value }: { icon: any; label: string; value: string }) => (
  <div className="flex items-center gap-2 text-sm">
    <Icon size={14} className="text-slate-400 shrink-0" />
    <span className="text-slate-400 font-medium">{label}:</span>
    <span className="text-slate-800 font-semibold truncate">{value}</span>
  </div>
);
